import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, CreditCard, Trash2, Lock, FileDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import { API_URL, auth, errMsg } from '../components/finance/financeApi';
import { OfferPanel } from '../components/finance/OfferPanel';
import { PayDialog } from '../components/finance/PayDialog';
import { ExpenseDialog } from '../components/finance/ExpenseDialog';
import { ExpenseCard } from '../components/finance/ExpenseCard';
import { ReportsPanel } from '../components/finance/ReportsPanel';
import { PaymentRow } from '../components/finance/PaymentRow';
import { FileViewer } from '../components/finance/FileViewer';
import { ConfirmDialog } from '../components/finance/ConfirmDialog';
import { VehicleStatusList } from '../components/finance/VehicleStatusList';
import { EftNotice } from '../components/finance/EftNotice';
import { ExportDialog } from '../components/finance/ExportDialog';
import { ParentPaymentsTab } from '../components/finance/parents/ParentPaymentsTab';

const ExpenseFilters = ({ role, vehicles, vehicle, setVehicle, scope, setScope }) => (
  <div className="flex flex-col sm:flex-row gap-2">
    {vehicles.length > 1 && (
      <Select value={vehicle} onValueChange={setVehicle}>
        <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="expense-filter-vehicle"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All vehicles</SelectItem>
          {vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.registration_number}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
    {role === 'transport' && (
      <Select value={scope} onValueChange={setScope}>
        <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="expense-filter-scope"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Everyone</SelectItem>
          <SelectItem value="drivers">Driver expenses</SelectItem>
          <SelectItem value="mine">My expenses</SelectItem>
        </SelectContent>
      </Select>
    )}
  </div>
);

export default function FinanceTracker() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [reports, setReports] = useState(null);
  const [payments, setPayments] = useState([]);
  const [vehicle, setVehicle] = useState('all');
  const [scope, setScope] = useState('all');
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [expenseDialog, setExpenseDialog] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [confirmTrialDelete, setConfirmTrialDelete] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [renewMode, setRenewMode] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('pay')) {
      setRenewMode(searchParams.get('pay') === 'renew');
      setShowPay(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    const params = { ...(vehicle !== 'all' ? { vehicle_id: vehicle } : {}) };
    try {
      const [s, e, r, p] = await Promise.all([
        axios.get(`${API_URL}/finance/status`, auth(token)),
        axios.get(`${API_URL}/finance/expenses`, { ...auth(token), params: { ...params, ...(scope !== 'all' ? { scope } : {}) } }),
        axios.get(`${API_URL}/finance/reports`, { ...auth(token), params }),
        axios.get(`${API_URL}/finance/payments`, auth(token)),
      ]);
      setStatus(s.data); setExpenses(e.data); setReports(r.data); setPayments(p.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load the Finance Tracker'));
    }
  }, [token, vehicle, scope]);

  useEffect(() => { load(); }, [load]);

  const startTrial = async (vehicleId) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/finance/trial`, { vehicle_id: vehicleId }, auth(token));
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not start the trial'));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const e = toDelete;
    setToDelete(null);
    try {
      await axios.delete(`${API_URL}/finance/expenses/${e.expense_id}`, auth(token));
      toast.success('Expense deleted');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete'));
    }
  };

  const deleteTrialData = async () => {
    setConfirmTrialDelete(false);
    try {
      const res = await axios.delete(`${API_URL}/finance/trial-data`, auth(token));
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not delete trial data'));
    }
  };

  if (!status) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  const addable = status.vehicles.filter((v) => v.can_add);
  const allOff = status.vehicles.length > 0 && status.vehicles.every((v) => v.disabled);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white" data-testid="finance-page">
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="finance-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-heading font-bold" data-testid="finance-title">Finance Tracker</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Petrol, repairs and tyres — every rand in one place</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            {addable.length > 0 && (
              <Button className="rounded-full" onClick={() => setExpenseDialog({})} data-testid="add-expense-btn"><Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add expense</span></Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 space-y-6">
        {!status.any_access && !allOff && <OfferPanel status={status} onStartTrial={startTrial} onPay={() => setShowPay(true)} busy={busy} />}
        {allOff && (
          <p className="flex items-center gap-2 text-sm rounded-xl bg-red-50 border border-red-200 text-red-800 p-4" data-testid="tracker-switched-off-note"><Lock className="w-4 h-4" /> The IT Administrator has switched the Finance Tracker off for your vehicle(s). Your saved records can still be viewed.</p>
        )}
        {!status.any_access && !allOff && expenses.length > 0 && (
          <p className="flex items-center gap-2 text-sm rounded-xl bg-slate-100 p-3" data-testid="tracker-locked-note"><Lock className="w-4 h-4" /> The tracker is locked, so new expenses can't be added. All your saved records are still here.</p>
        )}
        <VehicleStatusList status={status} />
        <div className="flex flex-wrap gap-2">
          {status.any_access && status.vehicles.length > 0 && (
            <Button variant="outline" className="rounded-full" onClick={() => setShowPay(true)} data-testid="pay-renew-btn"><CreditCard className="w-4 h-4 mr-1" /> Pay or renew (R199 per vehicle, EFT)</Button>
          )}
          {status.trial_records > 0 && (
            <Button variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setConfirmTrialDelete(true)} data-testid="delete-trial-data-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete my trial data</Button>
          )}
        </div>

        <Tabs defaultValue="expenses">
          <TabsList className="bg-white border flex-wrap h-auto" data-testid="finance-tabs">
            <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
            <TabsTrigger value="parents" data-testid="tab-parent-payments">Parent payments</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          </TabsList>
          <TabsContent value="expenses" className="space-y-3 mt-4">
            <ExpenseFilters role={status.role} vehicles={status.vehicles} vehicle={vehicle} setVehicle={setVehicle} scope={scope} setScope={setScope} />
            {expenses.length === 0 && <p className="text-sm text-muted-foreground p-4 bg-white rounded-xl border" data-testid="expenses-empty">No expenses yet.</p>}
            <div className="grid md:grid-cols-2 gap-3" data-testid="expense-list">
              {expenses.map((e) => (
                <ExpenseCard key={e.expense_id} e={e} role={status.role} token={token} onDone={load}
                  onEdit={(x) => setExpenseDialog({ expense: x })} onDelete={setToDelete}
                  onViewSlip={(x) => setViewFile({ kind: 'expense', id: x.expense_id, title: `Slip · ${x.date}` })} />
              ))}
            </div>
          </TabsContent>
          <TabsContent value="parents" className="mt-4">
            <ParentPaymentsTab role={status.role} token={token} onChanged={load} />
          </TabsContent>
          <TabsContent value="reports" className="mt-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
              <ExpenseFilters role="driver" vehicles={status.vehicles} vehicle={vehicle} setVehicle={setVehicle} />
              {status.role === 'transport' && (
                <Button variant="outline" className="rounded-full" onClick={() => setShowExport(true)} data-testid="export-open-btn"><FileDown className="w-4 h-4 mr-1" /> Download PDF / spreadsheet</Button>
              )}
            </div>
            <ReportsPanel reports={reports} />
          </TabsContent>
          <TabsContent value="payments" className="mt-4 space-y-3">
            <EftNotice />
            {payments.length === 0 && <p className="text-sm text-muted-foreground" data-testid="payments-empty">No payments yet.</p>}
            {payments.map((p) => <PaymentRow key={p.payment_id} p={p} onViewProof={(x) => setViewFile({ kind: 'payment', id: x.payment_id, title: `Proof · ${x.eft_reference}` })} />)}
          </TabsContent>
        </Tabs>
      </main>

      <PayDialog open={showPay} onOpenChange={(o) => { setShowPay(o); if (!o) setRenewMode(false); }} status={status} token={token} onDone={load} renew={renewMode} />
      {status.role === 'transport' && <ExportDialog open={showExport} onOpenChange={setShowExport} vehicles={status.vehicles} token={token} />}
      <ExpenseDialog open={!!expenseDialog} onOpenChange={(o) => { if (!o) setExpenseDialog(null); }} vehicles={addable} expense={expenseDialog?.expense} token={token} onDone={load} />
      <FileViewer file={viewFile} token={token} onClose={() => setViewFile(null)} />
      <ConfirmDialog open={!!toDelete} title="Delete this expense?" description="This can't be undone." confirmLabel="Yes, delete" onConfirm={doDelete} onCancel={() => setToDelete(null)} testid="delete-expense-confirm" />
      <ConfirmDialog open={confirmTrialDelete} title="Delete your trial data?" description={`All ${status.trial_records} expense(s) you saved during your free trial will be deleted. Expenses saved under a paid plan stay.`} confirmLabel="Yes, delete trial data" onConfirm={deleteTrialData} onCancel={() => setConfirmTrialDelete(false)} testid="delete-trial-confirm" />
    </div>
  );
}
